/**
 * storyDeleteFlow.js — F-252. The ONE ordering contract every "delete this story" path obeys.
 *
 * WHY THIS EXISTS: the contract was written in a comment (App.js:2129-2131 — "delete the ENTRY
 * FIRST and only proceed on a confirmed delete") and then applied by hand at four call sites. Two
 * of them got it wrong, in opposite directions:
 *
 *   - The archive path (App.js) deleted the entry and never removed the book page at all, while its
 *     own confirm copy promised "removed from your book and archive".
 *   - BookBuilder "Delete forever" removed the page and PERSISTED it BEFORE awaiting the delete,
 *     then announced "Chapter deleted." unconditionally — and because executeDeleteStory RETURNS
 *     false rather than throwing, its catch could never fire. A failed delete therefore left the
 *     story alive with its page gone AND told the user it had been deleted.
 *
 * A rule that each caller re-implements is a rule that drifts. This makes the ordering the
 * function, so a caller cannot get it wrong by forgetting, and so it can be tested directly
 * instead of only through a browser.
 *
 * CONTRACT:
 *   1. Delete the entry first. Nothing else happens until it is CONFIRMED.
 *   2. On failure: onFailure() and nothing else. No page removal, no success message, no
 *      navigation — the story is fully intact and the user can retry.
 *   3. On success: onDeleted() runs the page removal / navigation / success message.
 *   4. Returns true only on a confirmed delete.
 */
export async function runStoryDelete({ storyId, deleteEntry, onDeleted, onFailure }) {
  if (!storyId) return false;

  let deleted = false;
  try {
    deleted = await deleteEntry(storyId);
  } catch (err) {
    // executeDeleteStory swallows its own errors and returns false, but a caller could pass
    // something that throws. Either way it is a FAILED delete, never a silent success.
    deleted = false;
  }

  if (!deleted) {
    if (onFailure) onFailure();
    return false;
  }

  if (onDeleted) await onDeleted(storyId);
  return true;
}
